import { useState } from 'react';
import { Check, Copy, KeyRound, Plus } from 'lucide-react';
import { api } from '../../lib/api';
import { errorMessage, useApi } from '../../hooks/useApi';
import { Async, EmptyState, PageHeader } from '../../components/ui/States';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';

interface ApiKeyRow {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

const SCOPES: { id: string; help: string }[] = [
  { id: 'catalog:read', help: 'Browse titles and categories' },
  { id: 'playback:mint', help: 'Get video links for episodes a viewer may watch' },
  { id: 'events:write', help: 'Record what viewers watch' },
  { id: 'wallet:read', help: "Read a viewer's coin balance" },
  { id: 'wallet:write', help: "Change a viewer's coin balance" },
  { id: 'social:write', help: 'Likes, comments and shares' },
];

function fmt(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function status(k: ApiKeyRow): { label: string; variant: 'success' | 'danger' | 'warning' } {
  if (k.revokedAt) return { label: 'Revoked', variant: 'danger' };
  if (k.expiresAt && new Date(k.expiresAt).getTime() <= Date.now()) {
    return { label: 'Expired', variant: 'warning' };
  }
  return { label: 'Active', variant: 'success' };
}

export function ApiKeys() {
  const state = useApi<{ keys: ApiKeyRow[] }>('/admin/api-keys');
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<ApiKeyRow | null>(null);

  return (
    <div>
      <PageHeader
        title="API keys"
        subtitle="Keys let your app talk to Drameeka. Each key can only do what you allow it to."
        actions={
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-xs font-semibold text-white"
          >
            <Plus className="w-4 h-4" /> Create key
          </button>
        }
      />
      <Async state={state}>
        {({ keys }) =>
          keys.length === 0 ? (
            <EmptyState
              title="No API keys yet"
              message="Create a key to let your app load titles and play episodes."
            />
          ) : (
            <div className="bg-[#120d1a] border border-[#241b33] rounded-xl overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs uppercase tracking-wider text-gray-400 border-b border-[#241b33]">
                  <tr>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Key</th>
                    <th className="px-4 py-3">Can do</th>
                    <th className="px-4 py-3">Last used</th>
                    <th className="px-4 py-3">Created</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => {
                    const s = status(k);
                    return (
                      <tr key={k.id} className="border-b border-[#241b33] last:border-0 align-top">
                        <td className="px-4 py-3 text-white">{k.name || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-300">{k.keyPrefix}…</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {k.scopes.length === 0 ? (
                              <span className="text-gray-500">—</span>
                            ) : (
                              k.scopes.map((sc) => (
                                <Badge key={sc} variant="outline">
                                  {sc}
                                </Badge>
                              ))
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-300">{k.lastUsedAt ? fmt(k.lastUsedAt) : 'Never'}</td>
                        <td className="px-4 py-3 text-gray-300">{fmt(k.createdAt)}</td>
                        <td className="px-4 py-3">
                          <Badge variant={s.variant}>{s.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {!k.revokedAt && (
                            <button
                              onClick={() => setRevoking(k)}
                              className="text-xs font-semibold text-rose-400 hover:text-rose-300"
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        }
      </Async>

      <CreateModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(s) => {
          setCreating(false);
          setSecret(s);
          state.reload();
        }}
      />
      <SecretModal secret={secret} onClose={() => setSecret(null)} />
      <RevokeModal
        target={revoking}
        onClose={() => setRevoking(null)}
        onDone={() => {
          setRevoking(null);
          state.reload();
        }}
      />
    </div>
  );
}

function CreateModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (secret: string) => void;
}) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['catalog:read']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggle(id: string) {
    setScopes((cur) => (cur.includes(id) ? cur.filter((s) => s !== id) : [...cur, id]));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body: { name?: string; scopes: string[] } = { scopes };
      if (name.trim()) body.name = name.trim();
      const res = await api<{ secret: string }>('/admin/api-keys', { method: 'POST', body });
      setName('');
      setScopes(['catalog:read']);
      onCreated(res.secret);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => !o && !busy && onClose()}
      title="Create an API key"
      description="Choose what this key is allowed to do."
    >
      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <span className="text-xs text-gray-400">Name (optional)</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            placeholder="e.g. Mobile app"
            className="mt-1 w-full px-3 py-2 rounded-lg bg-[#0b0712] border border-[#241b33] text-sm text-white"
          />
        </label>
        <div className="space-y-2">
          {SCOPES.map((s) => (
            <label key={s.id} className="flex items-start gap-2 text-sm text-gray-200">
              <input
                type="checkbox"
                checked={scopes.includes(s.id)}
                onChange={() => toggle(s.id)}
                className="mt-1"
              />
              <span>
                <span className="font-mono text-xs text-violet-300">{s.id}</span>
                <span className="block text-xs text-gray-400">{s.help}</span>
              </span>
            </label>
          ))}
        </div>
        {error && (
          <p className="text-xs text-rose-400" role="alert">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-[#171022] border border-[#2c2140] text-xs font-semibold text-white"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || scopes.length === 0}
            title={scopes.length === 0 ? 'Choose at least one thing the key can do' : undefined}
            className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-xs font-semibold text-white"
          >
            {busy ? 'Creating…' : 'Create key'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function SecretModal({ secret, onClose }: { secret: string | null; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  async function copy() {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setCopyError(false);
    } catch {
      setCopyError(true);
    }
  }

  return (
    <Modal
      open={secret !== null}
      onOpenChange={(o) => {
        if (!o) {
          setCopied(false);
          setCopyError(false);
          onClose();
        }
      }}
      title="Your new API key"
      description="Copy it now. For your security it is never shown again."
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2 p-3 rounded-lg bg-[#0b0712] border border-[#241b33]">
          <KeyRound className="w-4 h-4 text-violet-400 shrink-0" />
          <code className="text-xs text-white break-all select-all">{secret}</code>
        </div>
        {copyError && (
          <p className="text-xs text-rose-400" role="alert">
            Could not copy automatically. Select the key above and copy it by hand.
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            onClick={copy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-xs font-semibold text-white"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-[#171022] border border-[#2c2140] text-xs font-semibold text-white"
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RevokeModal({
  target,
  onClose,
  onDone,
}: {
  target: ApiKeyRow | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/admin/api-keys/${target.id}`, { method: 'DELETE' });
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={target !== null}
      onOpenChange={(o) => {
        if (!o && !busy) {
          setError(null);
          onClose();
        }
      }}
      title="Revoke this key?"
      description="Anything using it will stop working straight away. This cannot be undone."
      maxWidth="md"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-200">
          {target?.name || 'Unnamed key'}{' '}
          <span className="font-mono text-xs text-gray-400">{target?.keyPrefix}…</span>
        </p>
        {error && (
          <p className="text-xs text-rose-400" role="alert">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-[#171022] border border-[#2c2140] text-xs font-semibold text-white"
          >
            Keep key
          </button>
          <button
            onClick={confirm}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-xs font-semibold text-white"
          >
            {busy ? 'Revoking…' : 'Revoke key'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
